import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Check, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import ActionModalShell from '@/components/ui/ActionModalShell';
import {
  FAYE_PRODUCT_SUMMARY_DISCLOSURE,
  FAYE_RETAILER_DISCLOSURE,
  FAYE_TRIP_COVERAGE_DISCLOSURE,
} from '../../../supabase/functions/_shared/faye-pricing.js';
import { FAYE_SUPPORT, premiumPerPaymentCents } from '../../../supabase/functions/_shared/trip-insurance.js';

const money = (cents) => `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Faye's wordmark from withfaye.com; follows the text color. */
export function FayeLogo({ className }) {
  return (
    <svg viewBox="0 0 149 75" className={className} role="img" aria-label="Faye" fill="currentColor">
      <path d="M120.417 40.0757L148.75 31.4392C147.917 20.9089 140.644 13.8635 130.19 13.8635C118.674 13.8635 110.72 22.5 110.72 34.1664C110.72 45.9847 118.902 54.5453 131.932 54.5453C136.553 54.5453 141.174 53.4848 145.72 50.9846V42.9544C141.477 45.8332 136.705 47.121 132.386 47.121C126.174 47.121 122.159 44.3938 120.417 40.0757ZM130.19 21.2878C134.356 21.2878 137.387 23.2575 138.977 26.9696L119.432 32.9545C119.811 26.0606 123.977 21.2878 130.19 21.2878Z" />
      <path d="M9.16658 23.8201V8.40909H33.5605V0H0V53.6363H9.16658V31.7104C15.53 32.9982 21.7423 32.7274 28.0301 29.6972V21.2122C21.7423 24.2424 15.4544 24.8049 9.16658 23.8201Z" />
      <path d="M64.4922 42.9544V28.3334C64.4922 18.9394 58.7347 13.8635 48.5074 13.8635C42.674 13.8635 37.977 15.5303 34.1893 18.1816V26.6666C37.9014 23.3334 42.8255 21.2121 47.977 21.2121C52.9014 21.2121 55.7042 23.1816 55.7042 26.7422C55.7042 28.8635 54.7193 29.0909 52.977 29.318L45.0983 30.3787C37.4466 31.3636 31.6133 34.9242 31.6133 42.5755C31.6133 50.1515 37.2952 54.5453 44.8709 54.5453C50.1741 54.5453 54.1133 52.4999 56.5376 48.6363C56.6891 50.1515 57.068 51.9695 57.5983 53.6363H66.3104C65.0226 50.1515 64.4922 46.515 64.4922 42.9544ZM55.7801 38.3333C55.7801 44.6968 51.2346 47.1969 46.8406 47.1969C43.1285 47.1969 40.4013 45.3787 40.4013 41.9695C40.4013 38.8634 42.674 37.0455 46.8406 36.5151L52.2195 35.7576C53.8862 35.5302 55.0226 35.2272 55.7801 34.6212V38.3333Z" />
      <path d="M96.6135 36.0605C96.6135 42.9544 93.3559 46.9695 87.9014 46.9695C83.5833 46.9695 80.0983 44.4696 80.0983 38.1059V14.7725H71.3862V39.3182C71.3862 49.3937 77.8259 54.5452 85.6287 54.5452C90.4772 54.5452 94.4167 52.5757 96.8409 48.7119V52.4242C96.8409 62.8787 90.2501 67.4242 82.144 67.4242C76.3106 67.4242 71.0717 65.0755 68.3444 59.8485H59.6323C63.0414 70.1514 72.2196 74.9999 82.144 74.9999C94.1893 74.9999 105.25 67.7272 105.25 52.6514V14.7725H96.6135V36.0605Z" />
    </svg>
  );
}
FayeLogo.propTypes = { className: PropTypes.string };

export const COMPARE_ROWS = [
  ['Trip cancellation', 'cancellation'],
  ['Trip interruption', 'interruption'],
  // A covered reason to cancel or interrupt: not whether delays are covered.
  ['Cancel or interrupt for bad weather, airline breakdowns or airport outages', 'weatherAndAirline'],
  ['Flight delay (6+ hrs)', 'delay'],
  ['Missed connection (3+ hrs)', 'missedConnection'],
  ['Emergency medical', 'medical'],
  ['Medical evacuation', 'evacuation'],
  ['Lost or damaged bags', 'baggage'],
];

/**
 * Faye travel insurance at checkout: one optional choice, priced into the
 * payments after the deposit. Picking a plan first opens the disclosures,
 * and "Accept" unlocks only once the customer has scrolled to their end;
 * after that, switching plans needs no second pass.
 *
 * Copy follows the approved mock and Faye's rules: describe coverage and
 * price only, coverage questions go to Faye, and nothing says when the
 * policy is bought.
 */
export default function FayeInsuranceOffer({ offer, choice, onChange, paymentsCount }) {
  const [sheet, setSheet] = useState(null); // 'compare' | 'details' | null
  const [pendingTier, setPendingTier] = useState(null);
  const [acceptedAt, setAcceptedAt] = useState(choice?.acceptedAt || null);
  const [readToEnd, setReadToEnd] = useState(false);
  const endRef = useRef(null);

  const accepting = sheet === 'details' && Boolean(pendingTier);

  // The accept button unlocks when the end of the disclosures scrolls into view.
  useEffect(() => {
    if (!accepting || !endRef.current || typeof IntersectionObserver === 'undefined') return undefined;
    setReadToEnd(false);
    const root = endRef.current.closest('.overflow-y-auto') || null;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setReadToEnd(true);
        observer.disconnect();
      }
    }, { root });
    observer.observe(endRef.current);
    return () => observer.disconnect();
  }, [accepting]);

  if (!offer?.eligible || !offer.plans?.length) return null;

  const selectedTier = choice?.tier || null;
  const declined = choice?.declined === true;
  const pendingPlan = offer.plans.find((p) => p.tier === pendingTier) || null;

  const choosePlan = (tier) => {
    if (acceptedAt) {
      onChange({ tier, acceptedAt });
      return;
    }
    setPendingTier(tier);
    setSheet('details');
  };
  const accept = () => {
    const at = new Date().toISOString();
    setAcceptedAt(at);
    onChange({ tier: pendingTier, acceptedAt: at });
    setPendingTier(null);
    setSheet(null);
  };
  const closeSheet = () => {
    setPendingTier(null);
    setSheet(null);
  };

  const optionClass = (active) => cn(
    'w-full text-left rounded-xl border px-3.5 py-3 transition-all duration-200',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/60',
    active
      ? 'border-brand-600 bg-brand-600/15 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]'
      : 'border-white/10 bg-white/[0.02] hover:border-white/25 hover:bg-white/[0.04]',
  );
  const dot = (active) => (
    <span className={cn(
      'mt-0.5 flex-shrink-0 inline-flex items-center justify-center w-4 h-4 rounded-full border transition-colors',
      active ? 'border-brand-600 bg-brand-600' : 'border-white/30',
    )}>
      {active && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3.5} />}
    </span>
  );

  return (
    <div className="mb-8 text-left max-w-2xl mx-auto">
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-brand-600/20 ring-1 ring-inset ring-brand-600/40">
            <ShieldCheck className="w-4 h-4 text-brand-300" strokeWidth={2.25} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <h3 className="text-[15px] sm:text-base font-semibold text-white leading-tight">Protect your trip</h3>
              <span className="text-[10px] uppercase tracking-wider font-medium text-white/55 bg-white/[0.06] rounded-full px-2 py-0.5">Optional</span>
              <span className="ml-auto inline-flex items-center gap-1.5 text-[11.5px] text-white/45 whitespace-nowrap">
                by <FayeLogo className="h-5 w-auto text-white" />
              </span>
            </div>
            <p className="mt-1.5 text-[13px] leading-relaxed text-white/70">
              Flight delayed 3+ hours: <strong className="font-medium text-white/90">free airport lounge</strong>.
            </p>
            <p className="text-[13px] leading-relaxed text-white/70">
              6+ hours: <strong className="font-medium text-white/90">$200 sent to your phone</strong>.
            </p>
          </div>
        </div>

        <div className="mt-4 space-y-2.5" role="radiogroup" aria-label="Travel insurance">
          {offer.plans.map((plan) => {
            const active = selectedTier === plan.tier;
            return (
              <button key={plan.tier} type="button" role="radio" aria-checked={active} onClick={() => choosePlan(plan.tier)} className={optionClass(active)}>
                <div className="flex items-start gap-2.5">
                  {dot(active)}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <span className={cn('text-[14px] font-medium leading-snug', active ? 'text-white' : 'text-white/90')}>{plan.label}</span>
                      <span className="text-right tabular-nums whitespace-nowrap">
                        <span className="block text-[14px] font-medium text-white">
                          +{money(premiumPerPaymentCents(plan.premiumCents, paymentsCount))}
                          <span className="text-[12px] font-normal text-white/55"> / payment</span>
                        </span>
                        <span className="block text-[12px] font-normal text-white/50">{money(plan.premiumCents)} total</span>
                      </span>
                    </div>
                    <ul className="mt-2 space-y-1">
                      {plan.highlights.map((h) => (
                        <li key={h} className="relative pl-3.5 text-[12.5px] text-white/55 before:content-[''] before:absolute before:left-0.5 before:top-[0.62em] before:w-1 before:h-1 before:rounded-full before:bg-white/30">
                          {h}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </button>
            );
          })}
          <button type="button" role="radio" aria-checked={declined} onClick={() => onChange({ declined: true })} className={optionClass(declined)}>
            <div className="flex items-center gap-2.5">
              {dot(declined)}
              <span className={cn('text-[13px] font-medium', declined ? 'text-white' : 'text-white/70')}>No thanks</span>
            </div>
          </button>
        </div>

        <p className="mt-3 text-[12.5px] text-white/55">Spread across your remaining payments, so your deposit doesn&rsquo;t change.</p>
        {selectedTier && (
          <p className="mt-1.5 text-[12.5px] text-emerald-300/90">
            {offer.plans.find((p) => p.tier === selectedTier)?.label} added. Faye issues it before you travel.
          </p>
        )}
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
          <button type="button" onClick={() => setSheet('compare')} className="text-[13px] text-brand-300 underline decoration-brand-300/40 underline-offset-[3px] hover:decoration-brand-300">Compare plans</button>
          <button type="button" onClick={() => { setPendingTier(null); setSheet('details'); }} className="text-[13px] text-brand-300 underline decoration-brand-300/40 underline-offset-[3px] hover:decoration-brand-300">Details &amp; disclosures</button>
        </div>
        <p className="mt-3 text-[11.5px] text-white/40">Not required to book. Defera isn&rsquo;t a licensed insurance agent; Faye answers coverage questions.</p>
      </div>

      <ActionModalShell
        isOpen={sheet === 'compare'}
        onClose={closeSheet}
        icon={ShieldCheck}
        title="Compare plans"
        description="Up to the amounts shown, per Faye's plan terms"
      >
        <table className="w-full text-[13px] tabular-nums">
          <thead>
            <tr className="border-b border-white/10">
              <th scope="col" className="py-2 text-left font-normal"><span className="sr-only">Benefit</span></th>
              {offer.plans.map((p) => (
                <th key={p.tier} scope="col" className="py-2 pl-3 text-right whitespace-nowrap text-[12px] font-medium text-white/60">{p.label.replace('Faye ', '').replace(' International', ' Intl')}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {COMPARE_ROWS.map(([name, key]) => (
              <tr key={key} className="border-b border-white/[0.06]">
                <td className="py-2 pr-2 text-white/60">{name}</td>
                {offer.plans.map((p) => {
                  const v = p.compare[key];
                  return (
                    <td key={p.tier} className={cn('py-2 pl-3 text-right whitespace-nowrap', v === true ? 'text-emerald-300' : v === false ? 'text-white/35' : 'text-white/85')}>
                      {v === true ? 'Yes' : v === false ? 'No' : v}
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr>
              <td className="pt-3 text-white font-medium">Added per payment</td>
              {offer.plans.map((p) => <td key={p.tier} className="pt-3 pl-3 text-right whitespace-nowrap text-white font-medium">+{money(premiumPerPaymentCents(p.premiumCents, paymentsCount))}</td>)}
            </tr>
            <tr>
              <td className="pt-1 text-white font-medium">Total for this trip</td>
              {offer.plans.map((p) => <td key={p.tier} className="pt-1 pl-3 text-right whitespace-nowrap text-white font-medium">{money(p.premiumCents)}</td>)}
            </tr>
          </tbody>
        </table>
        <p className="mt-3 text-[11.5px] text-white/45">Percentages are of non-refundable trip costs.</p>
      </ActionModalShell>

      <ActionModalShell
        isOpen={sheet === 'details'}
        onClose={closeSheet}
        icon={ShieldCheck}
        title={accepting && pendingPlan ? `Before you add ${pendingPlan.label}` : 'Details & disclosures'}
        description="Travel insurance by Faye"
        footer={accepting ? (
          <div className="space-y-2">
            <p className={cn('text-center text-[12px]', readToEnd ? 'text-emerald-300/90' : 'text-white/45')}>
              {readToEnd ? 'You’ve reached the end.' : 'Scroll to the end to accept.'}
            </p>
            <button
              type="button"
              onClick={accept}
              disabled={!readToEnd}
              className="w-full h-11 rounded-full bg-white text-black text-sm font-semibold transition-colors hover:bg-white/90 disabled:bg-white/10 disabled:text-white/40 disabled:cursor-not-allowed"
            >
              Accept and add {pendingPlan?.label}
            </button>
          </div>
        ) : null}
      >
        <dl className="space-y-3 text-[13px]">
          <div><dt className="text-[12px] font-medium text-white/55">When your policy is issued</dt><dd className="text-white/85">Faye issues your policy before you travel. Your policy number shows on your trip once your trip is paid in full.</dd></div>
          <div><dt className="text-[12px] font-medium text-white/55">If your trip is cancelled</dt><dd className="text-white/85">The cancel screen shows what happens to the travel insurance part of your payments; where it&rsquo;s refunded, it goes back to your card, not into Defera Credit.</dd></div>
          <div><dt className="text-[12px] font-medium text-white/55">Your deposit</dt><dd className="text-white/85">The plan&rsquo;s cost is spread across your remaining payments, with no Defera fees on it. Your deposit doesn&rsquo;t change.</dd></div>
          <div><dt className="text-[12px] font-medium text-white/55">Free look</dt><dd className="text-white/85">Once issued, you can review your policy and cancel it within 14 days if it&rsquo;s not right for you.</dd></div>
          <div><dt className="text-[12px] font-medium text-white/55">Questions about coverage</dt><dd className="text-white/85">Faye answers those: <span className="select-all">{FAYE_SUPPORT.email}</span> · <span className="select-all whitespace-nowrap">{FAYE_SUPPORT.phoneUs}</span></dd></div>
        </dl>
        <div className="mt-4 pt-3 border-t border-white/10 space-y-2 text-[11.5px] leading-relaxed text-white/50">
          <p>{FAYE_TRIP_COVERAGE_DISCLOSURE}</p>
          <p>{FAYE_RETAILER_DISCLOSURE}</p>
          <details open={accepting}>
            <summary className="cursor-pointer text-white/65">Full plan disclosure</summary>
            <p className="mt-2">{FAYE_PRODUCT_SUMMARY_DISCLOSURE}</p>
          </details>
        </div>
        <div ref={endRef} aria-hidden="true" className="h-px" />
      </ActionModalShell>
    </div>
  );
}

FayeInsuranceOffer.propTypes = {
  offer: PropTypes.object,
  choice: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  paymentsCount: PropTypes.number,
};
